// Library methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import CustomAutocomplete from "src/components/shared/inputs/CustomAutocomplete/CustomAutocomplete";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

// Types
import { PatientType } from "src/services/patients/types";
import { ExamType } from "src/services/exams/types";

// Utils
import { getTruncatedText } from "src/utils/text/textHelper";
import { isMultiExamPackage } from "src/utils/exams/examReportsHelper";

// Hooks
import { useExamsData } from "src/services/exams/hooks/useExamsData";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Constants
import { examCardEnums, ExamCardSectionNameType } from "src/constants/enums";

type LinkDeviceCardModalType = {
  open: boolean;
  onClose: () => void;
  onConfirm: (exam: ExamType) => void;
  patients: Array<PatientType>;
};

// Link modal that opens from the DeviceCardButton on the Patient/Dashboard device cards
const LinkDeviceCardModal = ({
  open,
  onClose,
  onConfirm,
  patients,
}: LinkDeviceCardModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { clinicInfo } = useClinicContext();

  const [selectedPatient, setSelectedPatient] = useState({
    displayName: "",
    value: "",
  });
  const [selectedExam, setSelectedExam] = useState({
    displayName: "",
    value: "",
  });

  const { data: examsData } = useExamsData(
    clinicInfo?.id,
    selectedPatient?.value
  );

  const formattedPatients = useMemo(
    () =>
      patients
        ?.map((option) => ({
          displayName: getTruncatedText(
            `${option?.firstName} ${option?.lastName}`,
            25
          ),
          value: option?.id,
        }))
        ?.sort((a, b) => a?.displayName?.localeCompare(b?.displayName)),
    [patients]
  );

  const getExamTypeLabel = useCallback(
    (exam: ExamType) => {
      const enumValues = Object.values(examCardEnums);

      if (isMultiExamPackage(exam)) {
        return t("Multi-Exam Package");
      } else {
        const examSectionKeys = Object.keys(exam).filter((key) =>
          key.includes("Sections")
        ) as Array<ExamCardSectionNameType>;

        const examLabel = enumValues.find(
          (val) => val.sectionName === examSectionKeys?.[0]
        )?.label;

        return examLabel ? t(examLabel) : "N/A";
      }
    },
    [t]
  );

  const formattedExams = useMemo(() => {
    const newArray: Array<{ displayName: string; value: string }> = [];

    // Sort incomplete exams using the most recent scheduled date and the type name
    const sortedExams = examsData
      ?.filter(
        (exam) =>
          exam.completionState !== "Completed" &&
          exam.completionState !== "Terminated" &&
          exam.completionState !== "Invalid"
      )
      ?.sort((a, b) => {
        const dateA = a?.scheduledDate?.slice(0, 10) ?? "N/A";
        const dateB = b?.scheduledDate?.slice(0, 10) ?? "N/A";
        const dateDiff = dateB?.localeCompare(dateA);

        if (dateDiff !== 0) return dateDiff;

        const typeA = getExamTypeLabel(a);
        const typeB = getExamTypeLabel(b);
        return typeA?.localeCompare(typeB);
      });

    sortedExams?.forEach((exam) => {
      let examTypeText = getExamTypeLabel(exam);
      const examDate = exam?.scheduledDate?.slice(0, 10) ?? "N/A";

      // If multiple of the same exam types are on the same day, add a number to the name
      // Example:  "Multi-Exam Package", "Multi-Exam Package #2",  "Multi-Exam Package #3"
      // This keeps each exam distinct in the dropdown selector
      const existingExams = newArray.filter(
        (data) =>
          data.displayName.includes(examTypeText) &&
          data.displayName.includes(examDate)
      );
      if (existingExams.length > 0) {
        examTypeText = examTypeText.concat(` #${existingExams.length + 1}`);
      }

      newArray.push({
        displayName: `${examTypeText} | ${examDate}`,
        value: exam?.id,
      });
    });

    return newArray;
  }, [examsData, getExamTypeLabel]);

  const handleSetPatient = useCallback(
    (item: { displayName: string; value: string }) => {
      const value = formattedPatients.find(
        (option) => option.value === item.value
      );

      if (value) {
        setSelectedPatient({ ...value });
        setSelectedExam({ displayName: "", value: "" });
      } else {
        setSelectedPatient({ displayName: "", value: "" });
        setSelectedExam({ displayName: "", value: "" });
      }
    },
    [formattedPatients]
  );

  const handleSetExam = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = formattedExams?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setSelectedExam({ ...value });
      }
    },
    [formattedExams]
  );

  const shouldDisable = useMemo(() => {
    const invalidPatients =
      formattedPatients.length === 0 ||
      (formattedPatients.length > 0 &&
        !selectedPatient.displayName &&
        !selectedPatient.value);

    const invalidExams =
      formattedExams.length === 0 ||
      (formattedExams.length > 0 &&
        !selectedExam.displayName &&
        !selectedExam.value);

    return invalidPatients || invalidExams;
  }, [formattedExams, formattedPatients, selectedExam, selectedPatient]);

  const resetValues = useCallback(() => {
    setSelectedExam({ displayName: "", value: "" });
    setSelectedPatient({ displayName: "", value: "" });
  }, []);

  const handleClose = useCallback(() => {
    resetValues();
    onClose();
  }, [resetValues, onClose]);

  const handleConfirm = useCallback(() => {
    const exam = examsData?.find((item) => item.id === selectedExam?.value);
    resetValues();
    if (exam) onConfirm(exam);
  }, [resetValues, onConfirm, selectedExam?.value, examsData]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={t("Link Device to Exam")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            sx={ModalCustomConfirm}
            disabled={shouldDisable}
            onClick={handleConfirm}
          >
            {t("Link")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="20px">
        {formattedPatients && formattedPatients?.length > 0 ? (
          <Box display="flex" flexDirection="column" gap="4px">
            <Typography fontSize="14px">
              {t("Patient")}
              <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
            </Typography>
            <CustomAutocomplete
              id="link-device-patient-autocomplete"
              data={formattedPatients}
              value={selectedPatient}
              onChange={handleSetPatient}
            />
          </Box>
        ) : (
          <Typography>{t("No patients found.")}</Typography>
        )}
        {selectedPatient?.displayName && selectedPatient?.value && (
          <Box display="flex" flexDirection="column" gap="4px">
            <Typography fontSize="14px">
              {t("Exam")}
              <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
            </Typography>
            <SelectDropdown
              id="link-device-exam-dropdown"
              required
              dropdownOptions={formattedExams}
              value={selectedExam?.displayName}
              handleChange={handleSetExam}
            />
          </Box>
        )}
      </Grid>
    </DefaultModal>
  );
};

export default LinkDeviceCardModal;
